import { getMediaUrl } from './getMediaUrl'

export interface ResolvedImage {
  src: string
  alt?: string
  srcSet?: string
  sizes?: string
}

export interface ImageSizes {
  thumbnail?: { url: string; width?: number; height?: number }
  mobile?: { url: string; width?: number; height?: number }
  tablet?: { url: string; width?: number; height?: number }
  desktop?: { url: string; width?: number; height?: number }
}

const resolveFromPossibleSizes = (
  sizes: unknown,
  fallbackAlt?: string,
  preferSize?: 'mobile' | 'tablet' | 'desktop' | 'thumbnail',
): ResolvedImage | null => {
  if (!sizes || typeof sizes !== 'object') return null

  const sizesObj = sizes as ImageSizes
  const sizeOrder = preferSize
    ? [preferSize, 'desktop', 'tablet', 'mobile', 'thumbnail']
    : ['desktop', 'tablet', 'mobile', 'thumbnail']

  // Helper to check if URL is valid (only check for literal "null" string)
  const isValidUrl = (url: string | null | undefined): boolean => {
    if (!url || typeof url !== 'string') return false
    const trimmed = url.trim()
    return trimmed !== '' && trimmed !== 'null' && trimmed !== '/null'
  }

  // Try to find preferred size first
  for (const sizeName of sizeOrder) {
    const size = sizesObj[sizeName as keyof ImageSizes]
    if (size && typeof size === 'object' && typeof size.url === 'string' && isValidUrl(size.url)) {
      const src = getMediaUrl(size.url)
      // Double-check that the resolved URL is valid
      if (src && src !== '') {
        const alt =
          typeof (size as any).alt === 'string'
            ? (size as any).alt
            : typeof fallbackAlt === 'string'
              ? fallbackAlt
              : undefined
        return { src, alt }
      }
    }
  }

  return null
}

/**
 * Builds a srcset string from available image sizes
 */
const buildSrcSet = (sizes: ImageSizes): string | undefined => {
  const srcSetParts: string[] = []

  // Helper to check if URL is valid (only check for literal "null" string)
  const isValidUrl = (url: string | null | undefined): boolean => {
    if (!url || typeof url !== 'string') return false
    const trimmed = url.trim()
    return trimmed !== '' && trimmed !== 'null' && trimmed !== '/null'
  }

  if (sizes.mobile?.url && isValidUrl(sizes.mobile.url)) {
    const url = getMediaUrl(sizes.mobile.url)
    if (url && url !== '') {
      const width = sizes.mobile.width || 768
      srcSetParts.push(`${url} ${width}w`)
    }
  }

  if (sizes.tablet?.url && isValidUrl(sizes.tablet.url)) {
    const url = getMediaUrl(sizes.tablet.url)
    if (url && url !== '') {
      const width = sizes.tablet.width || 1024
      srcSetParts.push(`${url} ${width}w`)
    }
  }

  if (sizes.desktop?.url && isValidUrl(sizes.desktop.url)) {
    const url = getMediaUrl(sizes.desktop.url)
    if (url && url !== '') {
      const width = sizes.desktop.width || 1920
      srcSetParts.push(`${url} ${width}w`)
    }
  }

  return srcSetParts.length > 0 ? srcSetParts.join(', ') : undefined
}

export const getImageData = (
  value: unknown,
  preferSize?: 'mobile' | 'tablet' | 'desktop' | 'thumbnail',
): ResolvedImage => {
  if (!value) {
    return { src: '' }
  }

  // If value is a string, it might be an ID or a URL
  // If it looks like an ID (not a URL), we can't resolve it without fetching
  // For now, treat it as a URL and return as-is (will be handled by getMediaUrl)
  if (typeof value === 'string') {
    // Check if it's literally "null" string
    const trimmed = value.trim()
    if (trimmed === '' || trimmed === 'null' || trimmed === '/null') {
      return { src: '' }
    }
    // If it's a URL (starts with http, https, or /), use it directly
    if (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('/')) {
      return { src: getMediaUrl(value) }
    }
    // If it looks like an ID, return empty (should be resolved to object by Payload depth)
    return { src: '' }
  }

  if (Array.isArray(value)) {
    const first = value.find(Boolean)
    return getImageData(first, preferSize)
  }

  if (typeof value !== 'object') {
    return { src: '' }
  }

  const record = value as Record<string, unknown>

  // Helper to check if URL is valid
  const isValidUrl = (url: string | null | undefined): boolean => {
    if (!url || typeof url !== 'string') return false
    const trimmed = url.trim()
    return trimmed !== '' && 
           trimmed !== 'null' && 
           !trimmed.includes('/null') && 
           !trimmed.endsWith('/null')
  }

  if (typeof record.src === 'string' && isValidUrl(record.src)) {
    return {
      src: record.src,
      alt: typeof record.alt === 'string' ? record.alt : undefined,
    }
  }

  if (typeof record.url === 'string' && isValidUrl(record.url)) {
    const src = getMediaUrl(record.url)
    // Double-check that resolved URL is valid
    if (!src || src === '') {
      return { src: '' }
    }

    // If sizes are available, build srcset
    let srcSet: string | undefined
    let sizes: string | undefined
    if (record.sizes && typeof record.sizes === 'object') {
      srcSet = buildSrcSet(record.sizes as ImageSizes)
      sizes = '(max-width: 768px) 100vw, (max-width: 1024px) 100vw, 1920px'
    }

    return {
      src,
      alt: typeof record.alt === 'string' ? record.alt : undefined,
      srcSet,
      sizes,
    }
  }

  if (record.sizes) {
    const resolved = resolveFromPossibleSizes(
      record.sizes, 
      record.alt as string | undefined, 
      preferSize
    )
    if (resolved) {
      // Build srcset if we have multiple sizes
      const srcSet = buildSrcSet(record.sizes as ImageSizes)
      const sizes = '(max-width: 768px) 100vw, (max-width: 1024px) 100vw, 1920px'
      return {
        ...resolved,
        srcSet: srcSet || resolved.srcSet,
        sizes: sizes || resolved.sizes,
      }
    }
  }

  if (record.image !== undefined) {
    const nested = getImageData(record.image, preferSize)
    return {
      src: nested.src,
      alt: typeof record.alt === 'string' ? record.alt : nested.alt,
      srcSet: nested.srcSet,
      sizes: nested.sizes,
    }
  }

  return { src: '' }
}
